import { z } from 'zod';
import { apiClient } from '@api/client';
import type { ApiResponseShape, CommonResponse } from '@/types/api';
import {
  varietyDetailSchema,
  varietyOptionSchema,
  varietySchema,
  type ListVarietiesParams,
  type Variety,
  type VarietyDetail,
  type VarietyFormValues,
  type VarietyOption,
} from './schema';

const BASE_PATH = '/varieties';

const listResponseSchema = z.object({
  message: z.string(),
  columnDefs: z.array(z.record(z.unknown())).optional(),
  rowData: z.array(varietySchema),
  gridOptions: z.record(z.unknown()).optional(),
  paginationDetails: z
    .object({
      page: z.number(),
      pageSize: z.number(),
      totalRecords: z.number(),
      isLastPage: z.boolean(),
    })
    .optional(),
});

const detailResponseSchema = z.object({
  statusCode: z.number(),
  message: z.string(),
  data: varietyDetailSchema.nullable(),
  error: z.string().nullable().optional(),
});

const recordResponseSchema = z.object({
  statusCode: z.number(),
  message: z.string(),
  data: varietySchema.nullable(),
  error: z.string().nullable().optional(),
});

const optionsResponseSchema = z.object({
  statusCode: z.number(),
  message: z.string(),
  data: z.array(varietyOptionSchema).nullable(),
  error: z.string().nullable().optional(),
});

function pruneParams(input: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(input)) {
    if (v === undefined || v === null || v === '') continue;
    out[k] = v;
  }
  return out;
}

function toWireTranslations(values: VarietyFormValues) {
  return values.translations
    .filter((t) => t.name.trim().length > 0)
    .map((t) => ({ localeCode: t.localeCode, name: t.name.trim() }));
}

function toCreatePayload(values: VarietyFormValues) {
  return {
    cropId: Number(values.cropId),
    code: values.code,
    name: values.name,
    imageStorageKey: values.imageStorageKey ?? undefined,
    thumbnailStorageKey: values.thumbnailStorageKey ?? undefined,
    isActive: values.isActive,
    translations: toWireTranslations(values),
  };
}

// `cropId` and `code` intentionally absent — both immutable post-create.
function toUpdatePayload(values: VarietyFormValues) {
  return {
    name: values.name,
    imageStorageKey: values.imageStorageKey,
    thumbnailStorageKey: values.thumbnailStorageKey,
    isActive: values.isActive,
    translations: toWireTranslations(values),
  };
}

export async function listVarieties(
  params: ListVarietiesParams,
): Promise<CommonResponse<Variety>> {
  const response = await apiClient.get(BASE_PATH, {
    params: pruneParams(params as unknown as Record<string, unknown>),
  });
  const parsed = listResponseSchema.safeParse(response.data);
  if (!parsed.success) {
    throw new Error(`Invalid list response: ${parsed.error.message}`);
  }
  return parsed.data;
}

export async function getVariety(id: string): Promise<VarietyDetail> {
  const response = await apiClient.get(`${BASE_PATH}/${id}`);
  const parsed = detailResponseSchema.safeParse(response.data);
  if (!parsed.success || !parsed.data.data) {
    throw new Error(parsed.success ? 'Empty response' : parsed.error.message);
  }
  return parsed.data.data;
}

export async function getVarietyOptions(): Promise<VarietyOption[]> {
  const response = await apiClient.get(`${BASE_PATH}/options`);
  const parsed = optionsResponseSchema.safeParse(response.data);
  if (!parsed.success || !parsed.data.data) {
    throw new Error(parsed.success ? 'Empty response' : parsed.error.message);
  }
  return parsed.data.data;
}

export async function createVariety(values: VarietyFormValues): Promise<Variety> {
  const response = await apiClient.post<ApiResponseShape<Variety>>(
    BASE_PATH,
    toCreatePayload(values),
  );
  const parsed = recordResponseSchema.safeParse(response.data);
  if (!parsed.success || !parsed.data.data) {
    throw new Error(parsed.success ? 'Empty response' : parsed.error.message);
  }
  return parsed.data.data;
}

export async function updateVariety(
  id: string,
  values: VarietyFormValues,
): Promise<Variety> {
  const response = await apiClient.put<ApiResponseShape<Variety>>(
    `${BASE_PATH}/${id}`,
    toUpdatePayload(values),
  );
  const parsed = recordResponseSchema.safeParse(response.data);
  if (!parsed.success || !parsed.data.data) {
    throw new Error(parsed.success ? 'Empty response' : parsed.error.message);
  }
  return parsed.data.data;
}
